/**
 * Ponte JavaScript WebView ↔ app de campo (Flutter, `flutter_inappwebview`) —
 * o contrato do HANDOFF «App de campo: secção Armazém» §6b. A página chama
 * `window.flutter_inappwebview.callHandler(nome, args)`, que devolve uma
 * Promise. Sem a ponte (a web normal), nada daqui é usado.
 *
 *   campoToken  —                                   → { token, expires_at, person }
 *   campoScan   { title, hint?, feedback?, counter? } → { code, format? } | { cancelled: true }
 *   campoToast  { variant, title, description? }    → —
 *   campoClose  —                                   → —
 *
 * Módulo puro (sem React nem `@/`): os testes de node importam-no.
 */

export type CampoTone = "ok" | "warn" | "error" | "info"

export interface CampoPerson {
  id: string
  name: string
  kind: "tecnico" | "armazem"
  can_manage_stock: boolean
}

export interface CampoTokenResult {
  token: string
  /** ISO 8601. */
  expires_at: string
  person: CampoPerson
}

export interface CampoScanInput {
  title: string
  hint?: string
  /** O resultado da leitura anterior, para o leitor nativo mostrar em cima. */
  feedback?: { tone: CampoTone; text: string }
  /** Extensão ao contrato (opcional, o Flutter pode ignorar): o contador do artigo aberto («3 séries», «12 Un»). */
  counter?: string
}

export type CampoScanResult = { code: string; format?: string } | { cancelled: true }

export interface CampoToastInput {
  variant: "info" | "success" | "error"
  title: string
  description?: string
}

export interface CampoBridge {
  token(): Promise<CampoTokenResult>
  scan(input: CampoScanInput): Promise<CampoScanResult>
  toast(input: CampoToastInput): Promise<void>
  close(): Promise<void>
}

interface InAppWebView {
  callHandler(name: string, ...args: unknown[]): Promise<unknown>
}

type BridgeWindow = {
  flutter_inappwebview?: InAppWebView
  addEventListener?: (type: string, fn: () => void, opts?: { once?: boolean }) => void
  removeEventListener?: (type: string, fn: () => void) => void
}

const READY_EVENT = "flutterInAppWebViewPlatformReady"

function win(): BridgeWindow | undefined {
  return typeof window === "undefined" ? undefined : (window as unknown as BridgeWindow)
}

/** A página está dentro da app de campo? (síncrono: o objeto existe desde o início da página). */
export function hasCampoBridge(w: BridgeWindow | undefined = win()): boolean {
  return !!w?.flutter_inappwebview
}

function wrap(raw: InAppWebView): CampoBridge {
  const call = (name: string, args?: unknown) => (args === undefined ? raw.callHandler(name) : raw.callHandler(name, args))
  return {
    token: async () => parseToken(await call("campoToken")),
    scan: async (input) => parseScan(await call("campoScan", input)),
    toast: async (input) => {
      await call("campoToast", input)
    },
    close: async () => {
      await call("campoClose")
    },
  }
}

/**
 * A ponte, quando a app já a ligou. O `flutter_inappwebview` só aceita
 * `callHandler` depois do evento `flutterInAppWebViewPlatformReady`; espera-o
 * até `timeoutMs` e devolve null fora da app de campo.
 */
export function waitForCampoBridge(timeoutMs = 2000, w: BridgeWindow | undefined = win()): Promise<CampoBridge | null> {
  const ready = () => (w?.flutter_inappwebview && typeof w.flutter_inappwebview.callHandler === "function" ? wrap(w.flutter_inappwebview) : null)
  const now = ready()
  if (now || !w?.addEventListener) return Promise.resolve(now)
  return new Promise((resolve) => {
    const onReady = () => {
      clearTimeout(timer)
      resolve(ready())
    }
    const timer = setTimeout(() => {
      w.removeEventListener?.(READY_EVENT, onReady)
      resolve(ready())
    }, timeoutMs)
    w.addEventListener!(READY_EVENT, onReady, { once: true })
  })
}

/** A ponte já pronta (para o leitor, chamado depois de a página arrancar). */
export function campoBridgeNow(w: BridgeWindow | undefined = win()): CampoBridge | null {
  return w?.flutter_inappwebview && typeof w.flutter_inappwebview.callHandler === "function" ? wrap(w.flutter_inappwebview) : null
}

export function parseToken(raw: unknown): CampoTokenResult {
  const r = (raw ?? {}) as { token?: unknown; expires_at?: unknown; person?: Partial<CampoPerson> }
  if (typeof r.token !== "string" || !r.token) throw new Error("A app de campo não deu o token do telemóvel.")
  const p: Partial<CampoPerson> = r.person ?? {}
  return {
    token: r.token,
    expires_at: typeof r.expires_at === "string" ? r.expires_at : "",
    person: {
      id: String(p.id ?? ""),
      name: String(p.name ?? ""),
      kind: p.kind === "armazem" ? "armazem" : "tecnico",
      can_manage_stock: p.can_manage_stock === true,
    },
  }
}

export function parseScan(raw: unknown): CampoScanResult {
  const r = (raw ?? {}) as { code?: unknown; format?: unknown; cancelled?: unknown }
  if (r.cancelled === true || typeof r.code !== "string") return { cancelled: true }
  return typeof r.format === "string" ? { code: r.code, format: r.format } : { code: r.code }
}

// ─── O ciclo do leitor nativo ───────────────────────────────────────────────

/** Uma ação de uma resposta, como o ciclo a vê (`ScanAction` do leitor). */
export interface LoopAction {
  label?: string
  /**
   * `false` (0.45.1): a ação não pede resposta já — o leitor continua e o
   * botão fica nas recentes da página (ex.: «Não é série — Guardar como
   * código de barras» numa série contada). Por omissão, pára o leitor.
   */
  blocking?: boolean
}

/** O que `onCode` devolve (o `ScanFeedback` do leitor, sem depender dele). */
export interface LoopFeedback {
  tone: CampoTone
  text: string
  action?: LoopAction
  actions?: LoopAction[]
}

function loopActions(fb: LoopFeedback | null | undefined): LoopAction[] {
  if (!fb) return []
  return [...(fb.action ? [fb.action] : []), ...(fb.actions ?? [])]
}

/**
 * Uma resposta com escolhas que pedem resposta já (botões): o leitor pára e a
 * página mostra-as. Só ações `blocking: false` não param o leitor.
 */
export function feedbackHasChoices(fb: LoopFeedback | null | undefined): boolean {
  return loopActions(fb).some((a) => a.blocking !== false)
}

/**
 * O texto que o leitor nativo mostra (só texto, sem botões): com ações que
 * não param o leitor, diz onde estão — «… — no ecrã: «Não é série — …»».
 */
export function loopFeedbackText(fb: LoopFeedback): string {
  const later = loopActions(fb).filter((a) => a.blocking === false && a.label)
  return later.length ? `${fb.text} — no ecrã: ${later.map((a) => `«${a.label}»`).join(" · ")}` : fb.text
}

export type LoopEnd =
  | { reason: "cancelled"; last: LoopFeedback | null }
  | { reason: "choices"; last: LoopFeedback }
  | { reason: "stopped"; last: LoopFeedback | null }
  | { reason: "error"; last: LoopFeedback | null; error: unknown }

/**
 * Lê em ciclo pelo leitor nativo (E2): abre com o título e a ajuda (e a
 * resposta da leitura anterior), passa o código ao `onCode` de sempre e volta
 * a abrir com a resposta dele. Termina quando quem lê cancela, quando uma
 * resposta pede uma escolha (a página mostra-a com os botões; uma ação
 * `blocking: false` não pára — fica nas recentes da página), quando
 * `stopped()` (o ecrã fechou) ou quando a ponte falha.
 */
export async function runNativeScanLoop(opts: {
  scan: (input: CampoScanInput) => Promise<CampoScanResult>
  onCode: (code: string) => Promise<LoopFeedback> | LoopFeedback
  /** Lido antes de cada abertura: o título/ajuda/contador mudam com o artigo aberto. */
  input: () => { title: string; hint?: string; counter?: string }
  stopped?: () => boolean
  /**
   * Depois de cada resposta, antes de voltar a abrir: deixa o ecrã atualizar
   * (o artigo que a leitura abriu muda o título e o contador do leitor).
   */
  settle?: () => Promise<void>
}): Promise<LoopEnd> {
  let last: LoopFeedback | null = null
  for (;;) {
    if (opts.stopped?.()) return { reason: "stopped", last }
    const base = opts.input()
    let res: CampoScanResult
    try {
      res = await opts.scan({
        title: base.title,
        ...(base.hint ? { hint: base.hint } : {}),
        ...(base.counter ? { counter: base.counter } : {}),
        ...(last ? { feedback: { tone: last.tone, text: loopFeedbackText(last) } } : {}),
      })
    } catch (error) {
      return { reason: "error", last, error }
    }
    if ("cancelled" in res) return { reason: "cancelled", last }
    if (opts.stopped?.()) return { reason: "stopped", last }
    const code = res.code.trim()
    if (!code) continue
    let fb: LoopFeedback
    try {
      fb = await opts.onCode(code)
    } catch (error) {
      fb = { tone: "error", text: (error as Error)?.message ?? String(error) }
    }
    last = fb
    if (feedbackHasChoices(fb)) return { reason: "choices", last: fb }
    await opts.settle?.()
  }
}
